import { useState } from 'react'
import { Typography, Button, Box } from '@mui/material'
import colors from 'styles/colors'
import { updateStates } from 'services/userServices'
import { getUserCurrentGoal } from 'utils/getSupportingInfo'
import RowStack from 'components/atoms/container/RowStack'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import { JOURNEY, ASSESSMENTS } from 'routes/constants'
import EastIcon from '@mui/icons-material/East'
import { IconContainer } from 'components/atoms/Icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export const AssessmentButton = ({ goal, btnSize, children, ...btnProps }) => {
  const navigate = useNavigate()
  const handleAssessmentClick = () => {
    navigate(`${ASSESSMENTS}/${goal.id}`, {
      state: {
        fromMyPath: true
      }
    })
  }
  return (
    <>
      {goal.hasAssessment && (
        <Button
          onClick={handleAssessmentClick}
          variant='outlined'
          color='primary'
          size={btnSize}
          {...btnProps}>
          {children}
        </Button>
      )}
    </>
  )
}

const PathCardActions = ({
  goal,
  isLibrary,
  openPreview,
  hideLeftButton = false,
  btnSize = 'large',
  btn1Props = {},
  btn2Props = {},
  containerProps = {},
  forOnboarding = false,
  onboardingCallback = () => {}
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [requestError, setRequestError] = useState(undefined)
  const [ongoingGoal, setOngoingGoal] = useState(undefined)

  const updateCurrentPath = async () => {
    const res = await updateStates(
      {
        currentGoalStepId: '',
        currentGoalId: goal.id
      },
      true
    )
    if (res.status === 200) {
      navigate(JOURNEY)
    } else {
      setRequestError(true)
    }
  }

  const onBeginPathClick = async e => {
    e.stopPropagation()
    const currentGoal = await getUserCurrentGoal()
    if (!currentGoal) {
      updateCurrentPath()
    } else {
      setOngoingGoal(currentGoal)
      setDialogOpen(true)
    }
  }

  const onDialogClose = confirmed => {
    if (confirmed) {
      updateCurrentPath()
    }
    setDialogOpen(false)
  }

  const handleGoalClick = () => {
    if (goal.opensAt) {
      openPreview && openPreview()
    } else if (goal.currentGoal) {
      navigate(JOURNEY)
    } else {
      navigate(`${JOURNEY}/${goal.id}`)
    }
  }
  const PathButton = ({ onClick, label, ...otherProps }) => {
    return (
      <Button
        onClick={onClick}
        color={
          goal.completed || goal.opensAt
            ? 'muted'
            : goal?.overDue
              ? 'systemError'
              : 'secondary'
        }
        size={btnSize}
        {...btn1Props}
        {...otherProps}>
        {label}
      </Button>
    )
  }
  return (
    <>
      {goal.licensed && (!goal.isLocked || goal.opensAt || goal.currentGoal) ? (
        <>
          <RowStack spacing={4} {...containerProps}>
            {forOnboarding ? (
              <PathButton
                onClick={() => onboardingCallback(goal)}
                label={t('onboarding.selectThisPath')}
                endIcon={<IconContainer icon={<EastIcon />} />}
              />
            ) : (
              !hideLeftButton &&
              (goal.currentGoal ||
              goal.completed ||
              goal.overDue ||
              goal.opensAt ? (
                <PathButton
                  onClick={handleGoalClick}
                  label={
                    goal?.completed
                      ? t('pathsAndModels.revisitPath')
                      : goal?.overDue
                        ? t('pathsAndModels.overdue')
                        : goal.currentGoal
                          ? t('pathsAndModels.jumpBackIn')
                          : goal.opensAt && t('home.previewPath')
                  }
                />
              ) : goal.active ? (
                <PathButton
                  onClick={handleGoalClick}
                  label={t('pathsAndModels.finishThisOff')}
                />
              ) : (
                <PathButton
                  onClick={onBeginPathClick}
                  label={t('pathsAndModels.beginPath')}
                />
              ))
            )}

            {!isLibrary && (
              <AssessmentButton goal={goal} btnSize={btnSize} {...btn2Props}>
                {t('pathsAndModels.assessments')}
              </AssessmentButton>
            )}
          </RowStack>
          {requestError === true && (
            <Box paddingX={2}>
              <RequestRetryAlert />
            </Box>
          )}
          <CommonDialog
            open={dialogOpen}
            onClose={onDialogClose}
            titleText={t('pathsAndModels.pathInProgress')}
            confirmText={t('pathsAndModels.confirmStartPath')}
            cancelText={t('common.cancel')}>
            <Typography>
              {t('pathsAndModels.startNewPath.part1')}
              <strong> '{ongoingGoal?.name}'</strong>
            </Typography>
            <Typography>{t('pathsAndModels.startNewPath.part2')}</Typography>
          </CommonDialog>
        </>
      ) : (
        <Typography variant='body1Small' sx={{ color: colors.MidnightTeal300 }}>
          {t('home.pathIsntAvailable')}
        </Typography>
      )}
    </>
  )
}

export default PathCardActions
